import {
  completeEcommerceStage,
  isEcommerceTransmissionPath,
  postEcommerceFormData,
} from '@/api/ecommerce';
import {
  Attachment,
  FORM_DATA_KEY_FACTORY,
  isValidSelfServiceId,
} from '@/api/getters';
import {
  ApiResponse,
  FormData,
  Stages,
  Steps,
  TypeOfDamage,
} from '@/api/types';
import { API_API_URL, API_URL, PRODUCT } from '@/consts';
import { LINE_TYPE, PLOT_TYPE } from '@/features/ecommerce/schema';

import { fakerPL as faker } from '@faker-js/faker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serialize } from 'object-to-formdata';
import { useLocation, useParams } from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setFormMutation = (id: string, data: Record<string, any>) => {
  if (!isValidSelfServiceId(id)) {
    return Promise.reject(new Error('Invalid id'));
  }
  return fetch(`${API_URL}/api/self-service/${id}`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });
};

export const updateEmail = (hash: string, email: string) => {
  return fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/email`,
    {
      method: 'PUT',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );
};

export const useFormMutation = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isEcommerce = isEcommerceTransmissionPath(pathname);

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (data: Record<string, any>) => {
      if (!isValidSelfServiceId(id))
        return Promise.reject(new Error('Invalid id'));

      if (isEcommerce) {
        const currentFormData = queryClient.getQueryData(
          FORM_DATA_KEY_FACTORY(id)
        ) as FormData | undefined;
        return postEcommerceFormData(id ?? '', data, currentFormData?.stage);
      }

      return setFormMutation(id ?? '', data);
    },
    retry: false,
    onSuccess: async (res) => {
      const formData = (await res.json()) as FormData;
      const currentFormData = queryClient.getQueryData(
        FORM_DATA_KEY_FACTORY(id)
      ) as FormData;

      const status = res.status;

      if (status === 200 && currentFormData.stage === formData.stage) {
        return;
      }

      if (status === 500 || currentFormData.stage !== formData.stage) {
        queryClient.invalidateQueries({
          queryKey: FORM_DATA_KEY_FACTORY(id),
        });
      }
    },
  });
};

export const useDeleteFileMutation = ({
  attachment_type_id,
}: {
  attachment_type_id: string | number;
}) => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      return fetch(`${API_URL}/api/self-service/${hash}/attachments/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (res, id) => {
      if (res)
        queryClient.setQueryData<Attachment[]>(
          ['attachments', hash, attachment_type_id],
          (data) => data?.filter((item) => item.id !== Number(id))
        );
    },
  });
};

export const useUpdateFilePasswordMutation = ({
  attachment_type_id,
}: {
  attachment_type_id: string | number;
}) => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => {
      return fetch(
        `${API_URL}/api/self-service/${encodeURIComponent(hash ?? '')}/attachments/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ password }),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    },
    onSuccess: (res, { id, password }) => {
      if (res)
        queryClient.setQueryData<Attachment[]>(
          ['attachments', hash, attachment_type_id],
          (data) =>
            data?.map((item) =>
              item.id === Number(id) ? { ...item, password } : item
            )
        );
    },
  });
};

export const completeStage = (
  hash: string | undefined,
  stage: string,
  isEcommerce = false
) => {
  if (isEcommerce) return completeEcommerceStage(hash ?? '', stage);

  return fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash ?? '')}/complete-stage`,
    {
      method: 'POST',
      body: JSON.stringify({ stage: stage }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );
};

export const useSubmitStagesMutation = ({
  afterGetInvalidate,
  withoutInvalidate,
}: { afterGetInvalidate?: () => void; withoutInvalidate?: boolean } = {}) => {
  const { id: hash } = useParams();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isEcommerce = isEcommerceTransmissionPath(pathname);

  return useMutation({
    mutationKey: ['submit-stages', hash],
    meta: { mutationType: 'submitStages' },
    mutationFn: (stage: Stages) => {
      return completeStage(hash, stage, isEcommerce);
    },
    onSuccess: () => {
      if (!withoutInvalidate) {
        queryClient
          .invalidateQueries({
            queryKey: FORM_DATA_KEY_FACTORY(hash),
          })
          .then(() => {
            setTimeout(() => {
              afterGetInvalidate && afterGetInvalidate();
            }, 300);
          });
      }
    },
  });
};

export const updateStep = async (hash: string, step: Steps | Stages) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/update-step`,
    {
      method: 'POST',
      body: JSON.stringify({ step: step }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update step');
  }

  return response.json();
};

export const useSubmitStepMutation = () => {
  const { id: hash } = useParams();

  return useMutation({
    mutationFn: (step: Steps | Stages) => updateStep(hash ?? '', step),
  });
};

export const useCreateLeadMutation = ({
  productId = 38,
  issueType,
}: {
  productId: number;
  issueType?: string | null;
}) => {
  const link = window.location.href;

  const url = link.split('/').slice(0, 3).join('/');

  const data = {
    page: '/analiza-kosztorysu/',
    referer: `${url}/analiza-kosztorysu/`,
    sent_from_url: `${url}/analiza-kosztorysu/`,
    first_name: faker.person.firstName(),
    email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
    phone: faker.helpers.replaceSymbolWithNumber('509######'),
    agreements: [
      'Akceptuję politykę prywatności oraz regulamin świadczenia usług przez Helpfind sp. z o.o.',
    ],
    productId: productId,
    additional_fields: {
      car_make:
        productId !== PRODUCT.HOUSE_HOLD && productId !== PRODUCT.TRANSMISSION
          ? faker.vehicle.manufacturer()
          : undefined,
      car_year:
        productId !== PRODUCT.HOUSE_HOLD && productId !== PRODUCT.TRANSMISSION
          ? faker.date.past({ years: 5 })
          : undefined,
      issueType: productId === PRODUCT.HOUSE_HOLD ? issueType : undefined,
      last_name:
        productId === PRODUCT.TRANSMISSION
          ? faker.person.lastName()
          : undefined,
      line_types:
        productId === PRODUCT.TRANSMISSION ? [LINE_TYPE.LOW] : undefined,
      plot_type:
        productId === PRODUCT.TRANSMISSION ? PLOT_TYPE.AGRICULTURAL : undefined,
      co_owners_count: productId === PRODUCT.TRANSMISSION ? '0' : undefined,
      do_not_send_messages:
        productId === PRODUCT.TRANSMISSION ? '1' : undefined,
    },
  };

  const formData = serialize(data);

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_API_URL}/api/hf/store`, {
        method: 'POST',
        body: formData,
        headers: {
          accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return await (response.json() as Promise<ApiResponse>);
    },
  });
};

export const updateOfferAccepted = async (
  hash: string,
  accepted: boolean,
  offerHash: string | null
) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/update-offer-accepted`,
    {
      method: 'POST',
      body: JSON.stringify({ accepted: accepted, hash: offerHash }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update step');
  }
  return response.json();
};

export const useSendOfferEnvelop = () => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sendDocumentEnvelop(hash ?? '', true),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
  });
};

export const sendDocumentEnvelop = async (hash: string, accepted: boolean) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/send-document-envelop`,
    {
      method: 'POST',
      body: JSON.stringify({ accepted: accepted }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update step');
  }
  return response.json();
};

export const useOfferAcceptedMutation = ({
  isHouseHold = false,
}: {
  isHouseHold?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  const { id: hash } = useParams();

  return useMutation({
    mutationFn: (offerHash: string) =>
      updateOfferAccepted(hash ?? '', true, offerHash),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
      if (isHouseHold) {
        updateStep(hash ?? '', Steps.Offer_finished);
      }
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
  });
};

export const useOfferRejectedMutation = () => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => updateOfferAccepted(hash ?? '', false, null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });

      updateStep(hash ?? '', Steps.Offer_finished);
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
  });
};

export interface CreateTestOfferParams {
  hash: string;
  isTax: 0 | 1;
  typeOfDamage?: TypeOfDamage;
}

export const createTestOffer = async ({
  hash,
  isTax,
  typeOfDamage,
}: CreateTestOfferParams) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/create-test-self-service-offer`,
    {
      method: 'POST',
      body: JSON.stringify({ is_tax: isTax, type_of_damage: typeOfDamage }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update step');
  }
  return response.json();
};

export const useCreateTestOfferMutation = () => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ isTax, typeOfDamage }: CreateTestOfferParams) =>
      createTestOffer({ hash: hash ?? '', isTax, typeOfDamage }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
  });
};

export const downloadOfferPdf = async ({
  hash,
  lead_id,
}: {
  hash: string;
  lead_id: number;
}) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${hash}/download-offer`,
    {
      method: 'POST',
      body: JSON.stringify({ lead_id }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to download offer: ${response.status} ${response.statusText}`
    );
  }

  return response.blob();
};

export const useDownloadOfferPdf = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { id: hash } = useParams();
  if (!hash && enabled) throw new Error('Hash is not provided');

  return useMutation({
    mutationFn: (lead_id: number) =>
      downloadOfferPdf({ hash: hash ?? '', lead_id }),
  });
};

export const rejectAllOffers = async (hash: string) => {
  const response = await fetch(
    `${API_URL}/api/self-service/${encodeURIComponent(hash)}/reject-all-offers`,
    {
      method: 'POST',
      body: JSON.stringify({ accepted: false }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update step');
  }
  return response.json();
};

export const useRejectAllOffersMutation = ({
  isHouseHold = false,
}: {
  isHouseHold?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  const { id: hash } = useParams();

  return useMutation({
    mutationFn: () => rejectAllOffers(hash ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
      if (isHouseHold) {
        updateStep(hash ?? '', Steps.Offer_rejected);
      }
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: FORM_DATA_KEY_FACTORY(hash),
      });
    },
  });
};
